<template>
    <v-container class="pa-5" fluid>
        <v-dialog
            v-model="toolboxPreviewDialog"
            eager
            scrollable
            max-width="600px"
            @keydown.esc="closeToolboxPreviewDialog"
            @click:outside="closeToolboxPreviewDialog"
        >
            <v-card tile>
                <v-card-text>
                    <img ref="toolboxImagePreview" class="image-preview">
                </v-card-text>
                <v-card-actions>
                    <v-btn data-sentry-label="Zamknij" text color="info" @click="closeToolboxPreviewDialog">
                        Zamknij
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <ParcelsDialog
            :is-open="parcelsDialog"
            :order-type="'cooperant'"
            :order-id="order.id"
            :delivery-data="currentDelivery"
            @on-save="refreshOrder"
            @on-close="closeParcelsDialog"
        />
        <SentManuallyDialog
            :is-open="sentManuallyDialog"
            @on-save="provideManuallyDeliveryNumber"
            @on-close="closeSentManuallyDialog"
        />

        <page-title>
            Zamówienie {{ order.shopOrderNumber }}
            <span :class="orderStatusClass" class="text-uppercase text-body-2">
                {{ orderStatus }}
                <v-tooltip v-if="order.isComplaint" :open-delay="tooltipDelay" top>
                    <template #activator="{on}">
                        <v-icon color="error" class="ml-2 mb-2" v-on="on">
                            mdi-alpha-r-circle
                        </v-icon>
                    </template>
                    <span>Zamówienie reklamacyjne</span>
                </v-tooltip>
            </span>
        </page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn text color="primary" class="pa-0" @click="close">
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Parametry zamówienia
                            </h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row>
                                            <v-col
                                                v-for="(value, key, index) in shopParameters"
                                                :key="index"
                                                cols="12"
                                                sm="4"
                                            >
                                                <v-text-field
                                                    class="mb-1"
                                                    :value="value"
                                                    :label="key"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    :readonly="!isCooperantOrderNumber(key)"
                                                    :background-color="customBackgroundColor(key)"
                                                    @input="updateValue($event)"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col
                                                v-for="(value, key, index) in sendingsParameters"
                                                :key="index"
                                                cols="12"
                                                sm="4"
                                            >
                                                <v-text-field
                                                    class="mb-1"
                                                    :value="value"
                                                    :label="key"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    :readonly="!isCooperantOrderNumber(key)"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col
                                                v-for="(value, key, index) in orderParameters"
                                                :key="index"
                                                cols="12"
                                                sm="3"
                                            >
                                                <template v-if="showTooltip(key)">
                                                    <v-text-field
                                                        class="mb-1"
                                                        :value="value"
                                                        :label="key"
                                                        :append-icon="showTooltip(key) ? 'mdi-plus-circle' : ''"
                                                        dense
                                                        readonly
                                                        outlined
                                                        hide-details
                                                    >
                                                        <template #append>
                                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                                <template #activator="{on}">
                                                                    <v-icon color="info" v-on="on">
                                                                        mdi-plus-circle
                                                                    </v-icon>
                                                                </template>
                                                                <span style="white-space: pre-line">{{ sortedPatterns
                                                                }}</span>
                                                            </v-tooltip>
                                                        </template>
                                                    </v-text-field>
                                                </template>
                                                <template v-else>
                                                    <v-text-field
                                                        class="mb-1"
                                                        :value="value"
                                                        :label="key"
                                                        dense
                                                        readonly
                                                        outlined
                                                        hide-details
                                                    />
                                                </template>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col
                                                v-for="(parameter, index) in productParameters"
                                                :key="index"
                                                cols="12"
                                                sm="4"
                                            >
                                                <v-text-field
                                                    class="mb-1"
                                                    :value="parameter.value"
                                                    :label="parameter.name"
                                                    dense
                                                    readonly
                                                    outlined
                                                    hide-details
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container v-if="showMultiplePatternsSection">
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Szczegóły zamówienia wielowzorowego
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-data-table
                                    :headers="orderVariantsHeaders"
                                    :items="order.variants"
                                    hide-default-footer
                                    disable-pagination
                                >
                                    <template #item.amount="{item}">
                                        {{ item.amount }}szt.
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Adresy do wysyłki
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-data-table
                                    v-model="labelsSelectedToPrint"
                                    :headers="orderAddressesHeaders"
                                    :items="deliveriesForSelection"
                                    :loading="actionLoading.saveOrder"
                                    item-key="id"
                                    show-select
                                    hide-default-footer
                                    disable-pagination
                                    :mobile-breakpoint="0"
                                    item-disabled="disabled"
                                    name="orderAddressesData"
                                >
                                    <template #header.data-table-select>
                                        <v-simple-checkbox
                                            :value="allEnabledDeliveriesSelected"
                                            :indeterminate="someEnabledDeliveriesSelected"
                                            @click.stop
                                            @input="toggleSelectAllDeliveries($event)"
                                        />
                                    </template>
                                    <template #item.data-table-select="{isSelected, select, item}">
                                        <v-simple-checkbox
                                            :value="isSelected"
                                            :disabled="item.disabled"
                                            @click.stop
                                            @input="select($event)"
                                        />
                                    </template>
                                    <template #item.address="{item}">
                                        {{ formatReceiverAddress(item) }}
                                    </template>
                                    <template #item.sender="{item}">
                                        {{ formatSenderAddress(item) }}
                                    </template>
                                    <template #item.quantity="{item}">
                                        {{ item.quantityToRead }} szt.
                                    </template>
                                    <template #item.patterns="{item}">
                                        <template v-if="deliveryPatterns(item).length">
                                            {{ formatDeliveryPattern(deliveryPatterns(item)[0]) }}

                                            <v-tooltip
                                                v-if="deliveryPatterns(item).length > 1"
                                                :open-delay="tooltipDelay"
                                                top
                                            >
                                                <template #activator="{on}">
                                                    <v-chip
                                                        small
                                                        class="ml-1"
                                                        color="warning"
                                                        v-on="on"
                                                    >
                                                        +{{ deliveryPatterns(item).length - 1 }}
                                                    </v-chip>
                                                </template>
                                                <div
                                                    v-for="(pattern, index) in deliveryPatterns(item).slice(1)"
                                                    :key="index"
                                                >
                                                    {{ formatDeliveryPattern(pattern) }}
                                                </div>
                                            </v-tooltip>
                                        </template>
                                    </template>
                                    <template #item.weight="{item}">
                                        {{ formatWeight(item.weight) }}
                                    </template>
                                    <template #item.parcels="{item}">
                                        {{ item.parcels.length }}
                                        <v-btn
                                            v-if="item.status == 10"
                                            text
                                            icon
                                            small
                                            data-sentry-label="Edytuj kartony"
                                            color="primary"
                                            @click="showParcelsDialog(item)"
                                        >
                                            <v-icon small>
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <template #item.status="{item}">
                                        {{ deliveryStatusName(item.status) }}
                                    </template>
                                    <template #item.manually="{item}">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    :disabled="item.status == 50"
                                                    text
                                                    icon
                                                    color="primary"
                                                    data-sentry-label="Nadaj ręcznie"
                                                    v-on="on"
                                                    @click="showSentManuallyDialog(item)"
                                                >
                                                    <v-icon>
                                                        mdi-truck
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Nadaj ręcznie</span>
                                        </v-tooltip>
                                    </template>
                                </v-data-table>

                                <v-divider />
                                <v-card-actions>
                                    <v-btn
                                        v-for="(carrier, index) in order.carriers"
                                        :key="carrier.id"
                                        :disabled="!carrier.sendingAvailable || !labelsSelectedToPrint.length"
                                        :loading="carrier._loading"
                                        color="info"
                                        :data-sentry-label="`Generuj etykietę ${carrier.name}`"
                                        outlined
                                        @click="printLabels(carrier, index)"
                                    >
                                        Generuj etykietę {{ carrier.name }}
                                        <v-icon right dark>
                                            mdi-printer
                                        </v-icon>
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container v-if="hasFilePreviews">
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Podgląd plików
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card class="preview">
                                <v-progress-linear color="blue" :indeterminate="actionLoading.toolbox" height="2" />
                                <v-tabs>
                                    <v-tab
                                        v-for="(template, index) in order.numberOfTemplates"
                                        :key="index"
                                        @click="getToolboxTemplateContent(order.toolBoxUrl, index + 1)"
                                    >
                                        Wzór {{ index + 1 }}
                                    </v-tab>
                                </v-tabs>
                                <v-divider />

                                <v-card-text>
                                    <div v-for="(module, key, index) in toolbox.previews" :key="index">
                                        <h3 class="text-subtitle-1 my-2">
                                            {{ $t('productComponents.' + key) }}
                                        </h3>
                                        <a
                                            v-for="(preview, previewIndex) in module"
                                            :key="previewIndex"
                                            class="thumbnail-preview"
                                            @click="openToolboxPreviewDialog(preview.image)"
                                        >
                                            <img :src="preview.thumb">
                                        </a>
                                    </div>
                                </v-card-text>
                                <v-divider />
                                <v-card-actions>
                                    <v-menu offset-y>
                                        <template #activator="{on}">
                                            <v-btn
                                                color="primary"
                                                text
                                                data-sentry-label="Pobranie plików produkcyjnych"
                                                :disabled="!toolbox.productionFiles"
                                                v-on="on"
                                            >
                                                Pliki produkcyjne
                                                <v-icon right dark>
                                                    mdi-download
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <v-list-item
                                                v-for="(productionFile, index) in toolbox.productionFiles"
                                                :key="index"
                                                :href="productionFile.link"
                                            >
                                                <v-list-item-title>
                                                    {{ productionFileName(productionFile) }}
                                                </v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Komentarze do zamówienia
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-list v-if="hasComments" two-line>
                                    <div v-for="(comment, index) in order.comments" :key="comment.createdAt">
                                        <v-list-item>
                                            <v-list-item-avatar>
                                                <v-avatar size="40px" color="blue-grey">
                                                    <span class="white--text text-uppercase">
                                                        {{ parseUserInitials(comment.username) }}
                                                    </span>
                                                </v-avatar>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ comment.comment }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ comment.username }} - {{ formatDate(comment.createdAt) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider :key="index" />
                                    </div>
                                </v-list>
                                <v-card-text v-else>
                                    <v-alert type="info" text border="left">
                                        Brak komentarzy do zamówienia.
                                    </v-alert>
                                </v-card-text>

                                <v-card-text v-if="!addCommentsBlocked">
                                    <v-textarea
                                        v-model="newComment"
                                        label="Dodaj komentarz"
                                        :disabled="actionLoading.addComment"
                                        auto-grow
                                        clearable
                                        hide-details
                                    />
                                    <v-row justify="end">
                                        <v-col>
                                            <v-btn
                                                color="primary"
                                                data-sentry-label="Wyślij komentarz"
                                                :disabled="newComment === null || newComment.length <= 0"
                                                :loading="actionLoading.addComment"
                                                @click="addComment"
                                            >
                                                Wyślij
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="success"
                                data-sentry-label="Zapisz"
                                :loading="actionLoading.saveOrder"
                                @click="saveOrder"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import ParcelsDialog from './../Common/ParcelsDialog'
import SentManuallyDialog from './../Common/SentManuallyDialog.vue'

import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'
import priceFormatter from '@/mixins/priceFormatter'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'

import { errorHandler } from '@/utils/errorHandler'
import { openPdfFile } from '@/utils/pdfHandler'

export default {
    name: 'CooperantOrder',
    components: {
        PageTitle,
        ParcelsDialog,
        SentManuallyDialog
    },
    mixins: [
        dateFormatter,
        parse,
        priceFormatter,
        tooltipDelay,
        weightFormatter
    ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('orders/getCooperantDeliveryStatusNames')
            .then(() => {
                store.dispatch('orders/getCooperantOrder', to.params.id)
                    .then(response => {
                        next(vm => {
                            vm.order = response.data

                            if (vm.hasFilePreviews) {
                                vm.getToolboxTemplateContent(vm.order.toolBoxUrl)
                            }
                        })
                    })
                    .catch(error => {
                        next(error)
                        errorHandler(error)
                    })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            order: {
                shipmentStatus: {
                    name: null
                },
                carriers: [],
                patterns: [],
            },
            toolbox: {
                previews: {},
                productionFiles: []
            },
            toolboxPreviewDialog: false,
            labelsSelectedToPrint: [],
            currentDelivery: {},
            parcelsDialog: false,
            sentManuallyDialog: false,
            newComment: '',
            actionLoading: {
                toolbox: false,
                addComment: false,
                saveOrder: false
            },
            orderAddressesHeaders: [
                {
                    text: 'Adres',
                    align: 'left',
                    sortable: false,
                    value: 'address'
                },
                {
                    text: 'Nadawca',
                    align: 'left',
                    sortable: false,
                    value: 'sender'
                },
                {
                    text: 'Nakład',
                    align: 'center',
                    sortable: false,
                    value: 'quantity'
                },
                {
                    text: 'Wzór',
                    align: 'center',
                    sortable: false,
                    value: 'patterns'
                },
                {
                    text: 'Waga przesyłki',
                    align: 'center',
                    sortable: false,
                    value: 'weight'
                },
                {
                    text: 'Ilość kartonów',
                    align: 'left',
                    sortable: false,
                    value: 'parcels'
                },
                {
                    text: 'Status',
                    align: 'center',
                    sortable: false,
                    value: 'status',
                    width: 140
                },
                {
                    text: 'Akcje',
                    align: 'left',
                    sortable: false,
                    value: 'manually'
                }
            ],
            orderVariantsHeaders: [
                {
                    text: 'Nazwa lub kolor',
                    align: 'left',
                    sortable: false,
                    value: 'name'
                },
                {
                    text: 'Ilość',
                    align: 'center',
                    sortable: false,
                    value: 'amount'
                },
                {
                    text: 'Kod',
                    align: 'center',
                    sortable: false,
                    value: 'code'
                },
            ]
        }
    },
    computed: {
        showMultiplePatternsSection() {
            return this.order?.variants?.length > 0
        },
        deliveriesForSelection() {
            const deliveries = this.order?.deliveries || []

            return deliveries.map(delivery => ({
                ...delivery,
                disabled: this.isDeliveryDisabled(delivery)
            }))
        },
        enabledDeliveriesForSelection() {
            return this.deliveriesForSelection.filter(delivery => !delivery.disabled)
        },
        selectedEnabledDeliveryIds() {
            return new Set((this.labelsSelectedToPrint || []).map(item => item.id))
        },
        allEnabledDeliveriesSelected() {
            const enabled = this.enabledDeliveriesForSelection

            if (!enabled.length) {
                return false
            }

            return enabled.every(item => this.selectedEnabledDeliveryIds.has(item.id))
        },
        someEnabledDeliveriesSelected() {
            const enabled = this.enabledDeliveriesForSelection

            if (!enabled.length) {
                return false
            }

            const selectedCount = enabled.reduce((acc, item) => acc + (this.selectedEnabledDeliveryIds.has(item.id) ? 1 : 0), 0)

            return selectedCount > 0 && selectedCount < enabled.length
        },
        sortedPatterns() {
            return [...this.order.patterns]
                .sort((a, b) => a.id - b.id)
                .map(pattern => `Wzór ${pattern.number} - ${pattern.quantity} szt.`)
                .join('\n')
        },
        hasComments() {
            return this.order.comments ? this.order.comments.length > 0 : false
        },
        hasFilePreviews() {
            return !!this.order.toolBoxUrl
        },
        addCommentsBlocked() {
            return this.order.domainUrl === null || this.order.shipmentStatus.status === 50
        },
        deliveryStatusNames() {
            return this.$store.state.orders.cooperantsOrder.cooperantDeliveryStatusNames
        },
        orderStatus() {
            let orderStatus = null

            this.order.overdue ? orderStatus = 'opóźnione' : orderStatus = this.order.shipmentStatus.name

            return orderStatus
        },
        orderStatusClass() {
            let orderStatusClass = ''

            if (this.order.overdue) {
                orderStatusClass = 'warning--text'

                return orderStatusClass
            }

            switch (this.order.shipmentStatus.status) {
                case 10:
                    orderStatusClass = 'info--text'
                    break
                case 50:
                    orderStatusClass = 'success--text'
                    break
                default:
                    break
            }

            return orderStatusClass
        },
        shopParameters() {
            return {
                'Nazwa sklepu': this.order.shopName,
                'Faktura': this.order.invoiceData,
                'Numer zamówienia kooperanta': this.order.cooperantNumber
            }
        },
        sendingsParameters() {
            return {
                'Data zamówienia': this.formatDate(this.order.createdAt),
                'Planowana data wysyłki': this.formatDate(this.order.plannedSendDate, false),
                'COD': this.order.isCOD ? 'Tak' : 'Nie',
            }
        },
        orderParameters() {
            return {
                'Produkt': this.order.shopProductName,
                'Nakład': `${this.order.quantity} szt.`,
                'Kod produktu': this.order.cooperantProductCode ? this.order.cooperantProductCode : '-',
                'Cena kooperanta': this.order.cooperantProductPrice ? `${this.formatPrice(this.order.cooperantProductPrice)} zł` : '-',
            }
        },
        productParameters() {
            const params = []

            if (this.order.parameters) {
                this.order.parameters.forEach(parameter => {
                    if (
                        parameter.parameterName !== 'template' &&
                        parameter.parameterName !== 'bleed' &&
                        parameter.parameterName !== 'package_value' &&
                        parameter.parameterName !== 'package_width' &&
                        parameter.parameterName !== 'package_height' &&
                        parameter.parameterName !== 'package_depth'
                    ) {

                        params.push({
                            name: parameter.parameterName,
                            value: parameter.parameterValueName
                        })
                    }
                })
            }

            return params
        },
        userName() {
            return this.$store.state.user.userName
        },
    },
    watch: {
        labelsSelectedToPrint: {
            deep: true,
            handler(val) {
                const filtered = (val || []).filter(item => !this.isDeliveryDisabled(item))

                if ((val || []).length !== filtered.length) {
                    this.labelsSelectedToPrint = filtered
                }
            }
        }
    },
    methods: {
        deliveryPatterns(item) {
            return Array.isArray(item.patterns) ? item.patterns : []
        },
        formatDeliveryPattern(pattern) {
            if (pattern === null || pattern === undefined) {
                return ''
            }

            if (typeof pattern !== 'object') {
                return pattern
            }

            const value = pattern.pattern ?? pattern.number ?? pattern.name ?? pattern.value

            return value === null || value === undefined ? '' : value
        },
        formatSenderAddress(item) {
            const fields = [
                item.senderCompanyName,
                item.senderFirstName && item.senderLastName ? `${item.senderFirstName} ${item.senderLastName}` : null,
                item.senderStreet,
                item.senderCity,
                item.senderZipCode,
                item.senderCountry,
                item.senderPhoneNumber
            ]

            return fields.filter(field => field && field.trim() !== '' && field.trim() !== '-').join(', ')
        },
        formatReceiverAddress(item) {
            const fields = [
                item.companyName,
                item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : null,
                item.street,
                item.city,
                item.zipCode,
                item.country,
                item.phoneNumber
            ]

            return fields.filter(field => field && field.trim() !== '' && field.trim() !== '-').join(', ')
        },
        showTooltip(key) {
            return key === 'Nakład' && this.order.patterns.length > 1
        },
        isCooperantOrderNumber(key) {
            return key === 'Numer zamówienia kooperanta' ? true : false
        },
        customBackgroundColor(name) {
            if (this.isCooperantOrderNumber(name)) {
                return 'green lighten-5'
            }
        },
        updateValue(value) {
            this.order.cooperantNumber = value
        },
        getOrder() {
            this.$store.dispatch('orders/getCooperantOrder', this.order.id)
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        refreshOrder(payload) {
            this.order = payload
        },
        async printLabels(carrier, index) {
            const printingDeliveriesIds = this.labelsSelectedToPrint.map(item => item.id)

            this.$set(this.order.carriers, index, { ...carrier, _loading: true })

            this.$store.dispatch('orders/printCooperantLabels', {
                printingDeliveriesIds,
                carrierId: carrier.id
            })
                .then(response => {
                    this.getOrder()
                    openPdfFile(response.data)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$set(this.order.carriers, index, { ...carrier, _loading: false })
                    this.labelsSelectedToPrint = []
                })
        },
        showParcelsDialog(delivery) {
            this.parcelsDialog = true
            this.currentDelivery = delivery
        },
        closeParcelsDialog() {
            this.parcelsDialog = false
            setTimeout(() => {
                this.currentDelivery = {}
            }, 500)
        },
        showSentManuallyDialog(item) {
            this.sentManuallyDialog = true
            this.currentDelivery = item
        },
        closeSentManuallyDialog() {
            this.sentManuallyDialog = false
            setTimeout(() => {
                this.currentDelivery = {}
            }, 500)
        },
        provideManuallyDeliveryNumber(value) {
            this.actionLoading.saveOrder = true
            this.$store.dispatch('orders/provideCooperationShipmentDeliveryNumber', {
                deliveryId: this.currentDelivery.id,
                deliveryNumber: value
            })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.getOrder()
                    this.actionLoading.saveOrder = false
                })
        },
        getToolboxTemplateContent(toolboxURI, templateId = 1) {
            this.actionLoading.toolbox = true
            this.$store.dispatch('orders/getToolboxTemplateContent', { toolboxURI, templateId })
                .then(response => {
                    this.toolbox = response.data.pattern[templateId]
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.toolbox = false
                })
        },
        openToolboxPreviewDialog(imageURI) {
            const image = this.$refs.toolboxImagePreview

            this.actionLoading.toolbox = true

            image.setAttribute('src', imageURI)
            image.onload = () => {
                this.toolboxPreviewDialog = true
                this.actionLoading.toolbox = false
            }
            image.onerror = () => {
                this.actionLoading.toolbox = false
            }
        },
        closeToolboxPreviewDialog() {
            this.toolboxPreviewDialog = false
        },
        addComment() {
            this.actionLoading.addComment = true

            this.$store.dispatch('orders/addCooperantComment', {
                comment: this.newComment,
                username: this.userName,
                orderId: this.order.id
            })
                .then(response => {
                    this.order.comments.push(response.data)
                    this.newComment = ''
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.addComment = false
                })
        },
        deliveryStatusName(statusId) {
            return this.deliveryStatusNames ? this.deliveryStatusNames.find(item => item.id == statusId).name : statusId
        },
        isDeliveryDisabled(item) {
            return item.status === 50 && item.sentByCarrierId === null
        },
        toggleSelectAllDeliveries(checked) {
            if (!checked) {
                this.labelsSelectedToPrint = []

                return
            }

            this.labelsSelectedToPrint = [...this.enabledDeliveriesForSelection]
        },
        productionFileName(file) {
            const words = file.link.split('/')

            return words[words.length - 1]
        },
        saveOrder() {
            this.actionLoading.saveOrder = true

            this.$store.dispatch('orders/updateOrder', {
                id: this.order.id,
                cooperantNumber: this.order.cooperantNumber,
            })
                .then(() => {
                    this.close()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveOrder = false
                })
        },
        close() {
            this.$router.push({ name: 'ordersCooperantOrders' })
                .catch(error => {
                    errorHandler(error)
                })
        },
    }
}
</script>
